<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue'

  defineProps<{
    websitePrice: number,
    designPrice: number,
    videoPrice: number,
    totalPrice: number,
    websiteMaintenance: number,
    designSocialMediaService: { price: number, frequency: string | null },
    videoServices: {
      videoMonthlyServicePrice: number,
      videoWeeklyServicePrice: number,
    },
    yearlyTotal: number,
    monthlyTotal: number,
    weeklyTotal: number,
  }>()

  const isExpanded = ref(false)
  const panelEl = ref<HTMLDivElement | null>(null)

  const toggleExpand = () => {
    isExpanded.value = !isExpanded.value
  }

  const refreshPage = () => {
    window.location.reload()
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      isExpanded.value &&
      panelEl.value &&
      !panelEl.value.contains(event.target as Node)
    ) {
      isExpanded.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<template>
  <div
    ref="panelEl"
    class="bg-secondary-1 w-full p-4 rounded-t-2xl shadow-lg">
    <!-- Main Content -->
    <div class="space-y-3">
      <!-- Expandable Details -->
      <transition
        enter-active-class="transition-all duration-300 ease-in overflow-hidden"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[500px] opacity-100"
        leave-active-class="transition-all duration-200 ease-out overflow-hidden"
        leave-from-class="max-h-[500px] opacity-100"
        leave-to-class="max-h-0 opacity-0">
        <div
          v-if="isExpanded"
          class="space-y-2 border-secondary-2/50 border-b pb-2">
          <h2 class="text-sm font-bold text-secondary-3">
            Summary
          </h2>
          <!-- Website & Apps Plan -->
          <div>
            <div class="flex justify-between text-sm text-secondary-3">
              <span>Website & Apps</span>
              <span class="font-bold">{{ websitePrice.toLocaleString() }}</span>
            </div>
            <div
              v-if="websiteMaintenance > 0"
              class="flex justify-between text-xs mt-1 pl-4">
              <span class="text-secondary-2 text-xs">+ Yearly Maintenance</span>
              <span class="font-bold text-secondary-2">{{ websiteMaintenance.toLocaleString() }}</span>
            </div>
          </div>
          <!-- Design Plan -->
          <div>
            <div class="flex justify-between text-sm text-secondary-3">
              <span>Design Plan</span>
              <span class="font-bold">{{ designPrice.toLocaleString() }}</span>
            </div>
            <div
              v-if="designSocialMediaService.price > 0"
              class="flex justify-between text-xs mt-1 pl-4">
              <span class="text-secondary-2 text-xs">
                + {{ designSocialMediaService.frequency === 'monthly' ? 'Monthly' : 'Weekly' }} Service
              </span>
              <span class="font-bold text-secondary-2">{{ designSocialMediaService.price.toLocaleString() }}</span>
            </div>
          </div>
          <!-- Video Plan -->
          <div>
            <div class="flex justify-between text-sm text-secondary-3">
              <span>Video Plan</span>
              <span
                v-if="!videoServices || (videoServices.videoMonthlyServicePrice === 0 && videoServices.videoWeeklyServicePrice === 0)"
                class="font-bold">{{ videoPrice.toLocaleString() }}</span>
            </div>
            <div
              v-if="videoServices.videoMonthlyServicePrice > 0"
              class="flex justify-between text-xs mt-1 pl-4">
              <span class="text-secondary-2 text-xs">+ Monthly Service{{ videoServices.videoMonthlyServicePrice > 0 && videoServices.videoWeeklyServicePrice > 0 ? 's' : '' }}</span>
              <span class="font-bold text-secondary-2">{{ videoServices.videoMonthlyServicePrice.toLocaleString() }}</span>
            </div>
            <div
              v-if="videoServices.videoWeeklyServicePrice > 0"
              class="flex justify-between text-xs mt-1 pl-4">
              <span class="text-secondary-2 text-xs">+ Weekly Service{{ videoServices.videoMonthlyServicePrice > 0 && videoServices.videoWeeklyServicePrice > 0 ? 's' : '' }}</span>
              <span class="font-bold text-secondary-2">{{ videoServices.videoWeeklyServicePrice.toLocaleString() }}</span>
            </div>
          </div>
          <hr
            v-if="yearlyTotal > 0 || monthlyTotal > 0"
            class="border-secondary-2/50 mt-2 pb-1">
          <!-- Totals -->
          <div
            v-if="yearlyTotal > 0"
            class="flex justify-between text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Yearly Total</span>
            <span class="font-bold text-secondary-2">{{ yearlyTotal.toLocaleString() }}</span>
          </div>
          <div
            v-if="monthlyTotal > 0"
            class="flex justify-between text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Monthly Total</span>
            <span class="font-bold text-secondary-2">{{ monthlyTotal.toLocaleString() }}</span>
          </div>
          <div
            v-if="weeklyTotal > 0"
            class="flex justify-between text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Weekly Total</span>
            <span class="font-bold text-secondary-2">{{ weeklyTotal.toLocaleString() }}</span>
          </div>
        </div>
      </transition>

      <!-- Estimated Budget -->
      <div class="flex gap-2">
        <button
          type="button"
          class="text-primary-700 text-xs font-medium hover:underline pt-0"
          @click="toggleExpand">
          <svg
            class="size-4 transition-transform duration-300"
            :class="{ 'rotate-180': isExpanded }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div class="w-full">
          <div class="flex justify-between font-bold text-sm items-center">
            <div>
              <span class="text-secondary-3">Estimated Budget:</span>
              <p class="text-xs text-secondary-2 font-normal">
                *all prices in IDR
              </p>
            </div>
            <span class="text-primary-700 text-sm">{{ totalPrice.toLocaleString() }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          class="text-primary-700 font-medium hover:underline text-xs border rounded-lg p-2 border-primary-700"
          @click="refreshPage">
          Reset
        </button>
        <button
          type="button"
          class="bg-primary-700 text-white rounded-lg font-medium transition hover:bg-primary-600 text-xs p-2">
          Consult Now
        </button>
      </div>
    </div>
  </div>
</template>

